"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.airportTimeZone = airportTimeZone;
exports.airportLocalTime = airportLocalTime;
exports.formatAirportLocalDateTime = formatAirportLocalDateTime;
/**
 * `airportTimeZone` / `airportLocalTime` — the ONE IATA → IANA zone answer.
 *
 * Hoisted from wallet-google's flightClassBuilder (2026-08-29) so the Apple
 * render paths can stamp arrival/departure wall times without importing the
 * Google package: wallet-core is the shared base layer. wallet-google
 * re-exports `airportTimeZone` unchanged, so its consumers did not move.
 *
 * Small static table covering the airports we serve. Update via the
 * airportia / OurAirports dataset when expanding partner footprint.
 */
const formatLocalDateTime_js_1 = require("./formatLocalDateTime.js");
const IATA_TO_IANA_TZ = {
    // CEE
    BUD: 'Europe/Budapest',
    PRG: 'Europe/Prague',
    WAW: 'Europe/Warsaw',
    KRK: 'Europe/Warsaw',
    OTP: 'Europe/Bucharest',
    SOF: 'Europe/Sofia',
    BEG: 'Europe/Belgrade',
    ZAG: 'Europe/Zagreb',
    LJU: 'Europe/Ljubljana',
    // Western EU
    FRA: 'Europe/Berlin',
    MUC: 'Europe/Berlin',
    BER: 'Europe/Berlin',
    HAM: 'Europe/Berlin',
    DUS: 'Europe/Berlin',
    CDG: 'Europe/Paris',
    ORY: 'Europe/Paris',
    AMS: 'Europe/Amsterdam',
    BRU: 'Europe/Brussels',
    ZRH: 'Europe/Zurich',
    GVA: 'Europe/Zurich',
    VIE: 'Europe/Vienna',
    LHR: 'Europe/London',
    LGW: 'Europe/London',
    STN: 'Europe/London',
    LTN: 'Europe/London',
    MAN: 'Europe/London',
    EDI: 'Europe/London',
    DUB: 'Europe/Dublin',
    // Mediterranean
    MAD: 'Europe/Madrid',
    BCN: 'Europe/Madrid',
    PMI: 'Europe/Madrid',
    AGP: 'Europe/Madrid',
    LIS: 'Europe/Lisbon',
    OPO: 'Europe/Lisbon',
    FCO: 'Europe/Rome',
    MXP: 'Europe/Rome',
    LIN: 'Europe/Rome',
    VCE: 'Europe/Rome',
    ATH: 'Europe/Athens',
    IST: 'Europe/Istanbul',
    // W6 2495 landed at MLA on 2026-08-28 and the table had no answer — the
    // Wizz Mediterranean holiday set belongs here.
    MLA: 'Europe/Malta',
    TLV: 'Asia/Jerusalem',
    // Nordic
    CPH: 'Europe/Copenhagen',
    ARN: 'Europe/Stockholm',
    OSL: 'Europe/Oslo',
    HEL: 'Europe/Helsinki',
    // Middle East / Africa
    DXB: 'Asia/Dubai',
    AUH: 'Asia/Dubai',
    DOH: 'Asia/Qatar',
    CAI: 'Africa/Cairo',
    CMN: 'Africa/Casablanca',
    // Americas
    JFK: 'America/New_York',
    LGA: 'America/New_York',
    EWR: 'America/New_York',
    BOS: 'America/New_York',
    IAD: 'America/New_York',
    ORD: 'America/Chicago',
    ATL: 'America/New_York',
    MIA: 'America/New_York',
    LAX: 'America/Los_Angeles',
    SFO: 'America/Los_Angeles',
    SEA: 'America/Los_Angeles',
    YYZ: 'America/Toronto',
    YUL: 'America/Toronto',
    YVR: 'America/Vancouver',
    // Asia
    HND: 'Asia/Tokyo',
    NRT: 'Asia/Tokyo',
    ICN: 'Asia/Seoul',
    PVG: 'Asia/Shanghai',
    PEK: 'Asia/Shanghai',
    HKG: 'Asia/Hong_Kong',
    SIN: 'Asia/Singapore',
    BKK: 'Asia/Bangkok',
    DEL: 'Asia/Kolkata',
    BOM: 'Asia/Kolkata',
    // Oceania
    SYD: 'Australia/Sydney',
    MEL: 'Australia/Melbourne',
    AKL: 'Pacific/Auckland',
};
/**
 * The IANA zone an airport keeps its clocks on, or undefined if we do not
 * know it. Exported because every pass surface needs exactly the same answer
 * — the Google FlightClass fields, the Google text modules, and the Apple
 * status wall times.
 */
function airportTimeZone(iata) {
    return IATA_TO_IANA_TZ[iata.toUpperCase()];
}
/**
 * `HH:mm` wall time at the airport for a UTC instant, or null when either
 * the instant is unparseable or the airport is unknown — a status line with
 * NO time beats one with a Budapest-clock time on a Tel Aviv arrival.
 */
function airportLocalTime(utcIso, iata) {
    const tz = airportTimeZone(iata);
    if (tz === undefined)
        return null;
    const ms = Date.parse(utcIso);
    if (!Number.isFinite(ms))
        return null;
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(new Date(ms));
    const pick = (k) => parts.find((p) => p.type === k)?.value ?? '00';
    const hh = pick('hour') === '24' ? '00' : pick('hour');
    return `${hh}:${pick('minute')}`;
}
// `YYYY-MM-DDTHH:mm[:ss[.sss]]` with NO offset — a wall time, not an instant.
const OFFSETLESS_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
/**
 * `YYYY-MM-DD HH:mm` on the AIRPORT's clock — the departure time a passenger
 * reads on the pass is local to where they board (a JFK departure printed in
 * Budapest time was six hours out). An airport we do not know gets UTC,
 * labelled: a wrong local time reads as fact, a labelled UTC time reads as
 * what it is (the same rule as the Google text modules). Empty, unparseable
 * and offset-less inputs pass through `formatLocalDateTime` untouched — they
 * carry no instant to relabel (the flightless travel seed feeds '').
 */
function formatAirportLocalDateTime(iso, iata) {
    const tz = airportTimeZone(iata);
    if (tz !== undefined)
        return (0, formatLocalDateTime_js_1.formatLocalDateTime)(iso, tz);
    if (OFFSETLESS_DATETIME_RE.test(iso) || Number.isNaN(Date.parse(iso))) {
        return (0, formatLocalDateTime_js_1.formatLocalDateTime)(iso);
    }
    return `${(0, formatLocalDateTime_js_1.formatLocalDateTime)(iso, 'UTC')} UTC`;
}
//# sourceMappingURL=airportTimeZones.js.map